import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowUp, Loader2, MessagesSquare, Plus, Trash2 } from 'lucide-react';
import { api } from '../lib/api';
import { cn, formatDate } from '../lib/utils';
import { Card, Disclosure, EmptyState, ICON, IconButton, Notice, PageHeader } from '../components/ui';

/**
 * Vragen: een gesprek met Claude over je werk, loon en de regels. Claude zoekt zelf op in de
 * app en in je documenten (worker/src/lib/vragen.ts). Elke stap is een eigen verzoek; deze
 * pagina roept /verder aan tot het antwoord er is en laat zien wat er wordt opgezocht.
 */

const VOORBEELDEN = [
  'Krijg ik toeslag voor de verlenging van maandag?',
  'Hoeveel verlof heb ik nog en wanneer vervalt het?',
  'Wanneer krijg ik mijn volgende loon en hoeveel ongeveer?',
  'Wat zegt de CAO over rust tussen twee diensten?',
];

/** Maximaal aantal stappen per vraag aan de kant van de pagina (de server stopt zelf eerder). */
const MAX_STAPPEN = 40;

const foutTekst = (e: unknown) => {
  const m = (e as Error).message ?? String(e);
  const json = m.match(/\{.*\}$/s)?.[0];
  if (json) {
    try {
      const p = JSON.parse(json) as { message?: string };
      if (p.message) return p.message;
    } catch {
      /* geen JSON */
    }
  }
  return m.slice(0, 200);
};

export function VragenPage() {
  const qc = useQueryClient();
  const [actief, setActief] = useState<number | null>(null);
  const [invoer, setInvoer] = useState('');
  const [bezig, setBezig] = useState<{ vraag: string; wat: string } | null>(null);
  const [fout, setFout] = useState<string | null>(null);
  const eind = useRef<HTMLDivElement>(null);

  const { data: lijst } = useQuery({ queryKey: ['vragen'], queryFn: api.vragen.lijst });
  const { data: gesprek } = useQuery({
    queryKey: ['vragen', actief],
    queryFn: () => api.vragen.gesprek(actief!),
    enabled: actief != null,
  });

  const verwijder = useMutation({
    mutationFn: (id: number) => api.vragen.verwijder(id),
    onSuccess: (_r, id) => {
      if (id === actief) setActief(null);
      qc.invalidateQueries({ queryKey: ['vragen'] });
    },
  });

  useEffect(() => {
    eind.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [gesprek?.berichten.length, bezig?.wat]);

  /** Stappen tot het antwoord er is. Alles staat op de server, dus na een fout kan het gewoon verder. */
  async function doorlopen(id: number, vraag: string) {
    setFout(null);
    setBezig({ vraag, wat: 'denkt na' });
    try {
      for (let i = 0; i < MAX_STAPPEN; i++) {
        const r = await api.vragen.verder(id);
        if (r.klaar) break;
        setBezig({ vraag, wat: r.wat });
      }
    } catch (e) {
      setFout(foutTekst(e));
    } finally {
      setBezig(null);
      await qc.invalidateQueries({ queryKey: ['vragen'] });
    }
  }

  async function verstuur(tekst: string) {
    const vraag = tekst.trim();
    if (!vraag || bezig) return;
    setInvoer('');
    setFout(null);
    try {
      setBezig({ vraag, wat: 'denkt na' });
      const { gesprek_id } = await api.vragen.stel(vraag, actief ?? undefined);
      if (gesprek_id !== actief) setActief(gesprek_id);
      await doorlopen(gesprek_id, vraag);
    } catch (e) {
      setBezig(null);
      setFout(foutTekst(e));
      setInvoer(vraag);
    }
  }

  const berichten = gesprek && gesprek.gesprek.id === actief ? gesprek.berichten : [];
  // De vraag die nu loopt staat nog niet in de opgehaalde berichten: tijdelijk tonen.
  const lopendeVraag = bezig && !berichten.some((b, i) => b.rol === 'user' && i === berichten.length - 1 && b.tekst === bezig.vraag) ? bezig.vraag : null;
  const onderbroken = !bezig && gesprek?.bezig && gesprek.gesprek.id === actief;

  return (
    <div className="page">
      <PageHeader
        title="Vragen"
        sub="Vraag alles over je diensten, loon, verlof en de regels. Claude zoekt het op in je documenten en in de app, en noemt de bron."
        actions={
          <button
            type="button"
            className="btn-secondary"
            disabled={!!bezig}
            onClick={() => {
              setActief(null);
              setFout(null);
            }}
          >
            <Plus className={ICON.sm} aria-hidden />
            Nieuw gesprek
          </button>
        }
      />

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-[minmax(0,1fr)_280px] [&>*]:min-w-0">
        <Card flush className="flex min-h-[60vh] flex-col">
          <div className="flex-1 space-y-5 px-4 py-5 sm:px-5" aria-live="polite">
            {!berichten.length && !lopendeVraag && (
              <div>
                <EmptyState icon={MessagesSquare} title="Stel een vraag" text="Bijvoorbeeld:" />
                <div className="mx-auto -mt-4 flex max-w-xl flex-wrap justify-center gap-2">
                  {VOORBEELDEN.map((v) => (
                    <button key={v} type="button" className="btn-secondary btn-sm" disabled={!!bezig} onClick={() => verstuur(v)}>
                      {v}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {berichten.map((b) => (b.rol === 'user' ? <Vraag key={b.id} tekst={b.tekst} /> : <Antwoord key={b.id} tekst={b.tekst} bronnen={b.bronnen} />))}
            {lopendeVraag && <Vraag tekst={lopendeVraag} />}
            {bezig && (
              <div className="flex items-center gap-2 text-sm text-slate-400" role="status">
                <Loader2 className={cn(ICON.sm, 'animate-spin text-brand-300')} aria-hidden />
                <span className="first-letter:uppercase">{bezig.wat}…</span>
              </div>
            )}
            {onderbroken && (
              <Notice tone="warn">
                Dit antwoord is niet afgemaakt.{' '}
                <button type="button" className="underline" onClick={() => doorlopen(actief!, '')}>
                  Verder gaan
                </button>
              </Notice>
            )}
            {fout && (
              <Notice tone="err">
                Er ging iets mis: {fout}{' '}
                {actief && (
                  <button type="button" className="underline" onClick={() => doorlopen(actief, '')}>
                    Opnieuw proberen
                  </button>
                )}
              </Notice>
            )}
            <div ref={eind} />
          </div>

          <form
            className="flex items-end gap-2 border-t border-surface-line p-3 sm:p-4"
            onSubmit={(e) => {
              e.preventDefault();
              verstuur(invoer);
            }}
          >
            <label htmlFor="vraag" className="sr-only">
              Je vraag
            </label>
            <textarea
              id="vraag"
              rows={1}
              className="input max-h-40 min-h-[44px] flex-1 resize-none py-2.5"
              placeholder={actief ? 'Stel een vervolgvraag' : 'Stel je vraag'}
              value={invoer}
              disabled={!!bezig}
              onChange={(e) => {
                setInvoer(e.target.value);
                e.target.style.height = 'auto';
                e.target.style.height = `${e.target.scrollHeight}px`;
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  verstuur(invoer);
                }
              }}
            />
            <button type="submit" className="btn-primary h-11 w-11 shrink-0 !p-0" disabled={!invoer.trim() || !!bezig} aria-label="Versturen">
              {bezig ? <Loader2 className={cn(ICON.sm, 'animate-spin')} aria-hidden /> : <ArrowUp className={ICON.sm} aria-hidden />}
            </button>
          </form>
        </Card>

        <GesprekkenLijst
          lijst={lijst?.gesprekken ?? []}
          actief={actief}
          bezig={!!bezig}
          onKies={(id) => {
            setActief(id);
            setFout(null);
          }}
          onVerwijder={(id) => verwijder.mutate(id)}
        />
      </div>
    </div>
  );
}

function GesprekkenLijst(props: {
  lijst: Array<{ id: number; titel: string; updated_at: string }>;
  actief: number | null;
  bezig: boolean;
  onKies: (id: number) => void;
  onVerwijder: (id: number) => void;
}) {
  const inhoud = props.lijst.length ? (
    <div className="list">
      {props.lijst.map((g) => (
        <div key={g.id} className={cn('row group pr-2', g.id === props.actief && 'bg-white/[0.04]')}>
          <button type="button" className="min-w-0 flex-1 text-left" disabled={props.bezig} onClick={() => props.onKies(g.id)}>
            <div className={cn('truncate text-sm', g.id === props.actief ? 'font-medium text-slate-50' : 'text-slate-200')}>{g.titel}</div>
            <div className="t-num text-xs text-slate-500">{formatDate(g.updated_at.replace(' ', 'T') + 'Z', 'dd-MM HH:mm')}</div>
          </button>
          <IconButton icon={Trash2} label="Gesprek verwijderen" disabled={props.bezig} onClick={() => props.onVerwijder(g.id)} className="shrink-0 text-slate-500" />
        </div>
      ))}
    </div>
  ) : (
    <p className="px-4 py-3 text-sm text-slate-500 sm:px-5">Nog geen gesprekken.</p>
  );
  return (
    <>
      <Card flush title="Gesprekken" icon={MessagesSquare} className="hidden self-start lg:block">
        <div className="border-t border-surface-line">{inhoud}</div>
      </Card>
      <div className="card-flush lg:hidden">
        <Disclosure title="Eerdere gesprekken" right={props.lijst.length ? <span className="badge-count">{props.lijst.length}</span> : undefined}>
          <div className="-mx-4 sm:-mx-5">{inhoud}</div>
        </Disclosure>
      </div>
    </>
  );
}

function Vraag({ tekst }: { tekst: string }) {
  return (
    <div className="flex justify-end">
      <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-surface-raised px-3.5 py-2 text-sm text-slate-100">{tekst}</div>
    </div>
  );
}

function Antwoord({ tekst, bronnen }: { tekst: string; bronnen: string[] }) {
  return (
    <div className="max-w-[92%] space-y-2 text-sm leading-relaxed text-slate-200">
      <OpgemaakteTekst tekst={tekst} />
      {!!bronnen.length && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {bronnen.map((b) => (
            <span key={b} className="rounded-full bg-white/[0.05] px-2 py-0.5 text-xs text-slate-400">
              {b}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** Alinea's, lijstjes met "- " en **vet**; verder gewone tekst. */
function OpgemaakteTekst({ tekst }: { tekst: string }) {
  const delen: ReactNode[] = [];
  tekst.split(/\n{2,}/).forEach((alinea, i) => {
    const regels = alinea.split('\n');
    if (regels.every((r) => /^\s*[-•*] /.test(r))) {
      delen.push(
        <ul key={i} className="list-disc space-y-1 pl-5">
          {regels.map((r, j) => (
            <li key={j}>{vet(r.replace(/^\s*[-•*] /, ''))}</li>
          ))}
        </ul>
      );
    } else {
      delen.push(
        <p key={i} className="whitespace-pre-wrap break-words">
          {vet(alinea)}
        </p>
      );
    }
  });
  return <>{delen}</>;
}

function vet(s: string): ReactNode[] {
  return s.split(/(\*\*[^*]+\*\*)/g).map((stuk, i) =>
    stuk.startsWith('**') && stuk.endsWith('**') ? (
      <strong key={i} className="font-semibold text-slate-50">
        {stuk.slice(2, -2)}
      </strong>
    ) : (
      stuk
    )
  );
}
